// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Heading from 'ui/atoms/Heading'
import Space from 'ui/atoms/Space'
import Checkbox from 'ui/Checkbox'
import Dropdown from 'ui/Dropdown'
import TextAreaField from 'ui/molecules/TextAreaField'
import TextField from 'ui/molecules/TextField'
import ToggleField from 'ui/molecules/ToggleField'
import { QuestionTitle } from 'ui/Text'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'
import useBuilderQuestionOptions from 'utils/FormBuilder/hooks/useBuilderQuestionOptions'
import useQuestionTypeLabel from 'utils/FormBuilder/hooks/useQuestionTypeLabel'
import { ZentakeContext } from 'utils/ZentakeContext'

import BuilderSideMenuConditionalRules from './BuilderSideMenuConditionalRules'
import BuilderSideMenuDefaultValue from './BuilderSideMenuDefaultValue'
import BuilderSideMenuDemographicsOptions from './BuilderSideMenuDemographicsOptions'
import BuilderSideMenuEditQuestionProps from './BuilderSideMenuEditQuestionProps'
import BuilderSideMenuEditQuestionSubtype from './BuilderSideMenuEditQuestionSubtype'

function BuilderSideMenuEditQuestion(): React.Node {
  const { selectedItem, setQuestion, questionsArray, questionValidationTypes } =
    React.useContext(FormBuilderContext)

  const { setAlertConfig } = React.useContext(ZentakeContext)

  const question = useBuilderQuestion(selectedItem?.id)

  const questionType =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_type
      : ''

  const questionSubtype =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_subtype
      : ''

  const {
    hasTitle,
    hasHelperText,
    hasRequiredAndOfficeUse,
    hasDemographics,
    hasAssignValues,
  } = useBuilderQuestionOptions(questionSubtype, questionType)

  const questionTypeLabel = useQuestionTypeLabel(questionType, questionSubtype)

  const [questionTitle, setQuestionTitle] = React.useState('')

  React.useEffect(() => {
    if (question) {
      setQuestionTitle(question.text)
    }
  }, [question])

  const onChangeQuestionTitle = React.useCallback(
    (nextTitle) => {
      const nextTitleToSave = nextTitle.replace(/\n/g, '')
      setQuestionTitle(nextTitleToSave)
      setQuestion(selectedItem.id, {
        ...question,
        text: nextTitleToSave,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const onChangeQuestionText = React.useCallback(
    (nextText) => {
      setQuestion(selectedItem.id, {
        ...question,
        help_text: nextText,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const onChangeQuestionIsRequired = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      is_required: !question?.is_required,
      is_read_only:
        questionSubtype === 'handout' ? true : question?.is_read_only,
    })
  }, [question, questionSubtype, selectedItem.id, setQuestion])

  const isQuestionInCalculation = React.useCallback(() => {
    const calculationQuestions = questionsArray.filter(
      (question) => question.question_type?.question_subtype === 'calculation'
    )

    return calculationQuestions.some((calculationQuestion) =>
      calculationQuestion.formula.includes(`Q${question.form_index}`)
    )
  }, [question, questionsArray])

  const willProcceedToDisableAssignValues = React.useCallback(() => {
    return new Promise((resolve) =>
      setAlertConfig({
        buttons: [
          {
            onPress: () => resolve(false),
            style: 'secondary',
            title: 'Cancel',
          },
          {
            onPress: () => resolve(true),
            style: 'primary',
            title: 'Continue',
          },
        ],
        description:
          "This field is connected to a calculation. Turning off 'Assign Values' may affect the calculation's accuracy. Are you sure you want to proceed?",
        title: 'Warning',
      })
    )
  }, [setAlertConfig])

  const onChangeQuestionAssignValues = React.useCallback(async () => {
    if (question?.assign_values && isQuestionInCalculation()) {
      const proceed = await willProcceedToDisableAssignValues()
      if (!proceed) return
    }

    setQuestion(selectedItem.id, {
      ...question,
      assign_values: !question?.assign_values,
    })
  }, [
    isQuestionInCalculation,
    question,
    selectedItem,
    setQuestion,
    willProcceedToDisableAssignValues,
  ])

  const onChangeIncludeMiddleName = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      include_middle_name: !question?.include_middle_name,
    })
  }, [question, selectedItem, setQuestion])

  const onChangeQuestionValidationType = React.useCallback(
    (nextValue) => {
      setQuestion(selectedItem.id, {
        ...question,
        validation_type: nextValue,
      })
    },
    [question, selectedItem, setQuestion]
  )

  return (
    <Space size="large">
      <Heading className="mt-3">Field Settings - {questionTypeLabel}</Heading>
      <Space>
        {hasTitle && (
          <TextAreaField
            skipOwnState
            initialHeight={40}
            label="Question Text"
            maxHeight={160}
            name="question-title"
            onChange={onChangeQuestionTitle}
            placeholder="Question Text"
            value={questionTitle}
          />
        )}
        {hasHelperText && (
          <TextField
            label="Help Text"
            name="question-helper"
            onChange={onChangeQuestionText}
            placeholder="Help Text"
            value={question?.help_text ?? ''}
          />
        )}
        {questionSubtype === 'full_name' && (
          <>
            <QuestionTitle>Include</QuestionTitle>
            <Checkbox
              checked={!!question?.include_middle_name}
              label="Middle Name"
              onChange={onChangeIncludeMiddleName}
            />
          </>
        )}
        <BuilderSideMenuEditQuestionSubtype />
        {hasRequiredAndOfficeUse && (
          <ToggleField
            horizontal
            primary
            label={
              questionSubtype === 'handout'
                ? 'Required to Download'
                : 'Required'
            }
            onChange={onChangeQuestionIsRequired}
            value={Boolean(question?.is_required)}
          />
        )}
        {hasAssignValues && (
          <ToggleField
            horizontal
            primary
            label="Assign Values"
            onChange={onChangeQuestionAssignValues}
            value={Boolean(question?.assign_values)}
          />
        )}
        <BuilderSideMenuEditQuestionProps hasAssignValues={hasAssignValues} />
        {!['summary'].includes(questionType) && (
          <BuilderSideMenuConditionalRules />
        )}
        {[
          'single_line',
          'email',
          'multi_line',
          'phone',
          'month_year',
          'date_only',
          'time_only',
          'datetime',
          'number',
        ].includes(question?.question_type?.question_subtype) && (
          <BuilderSideMenuDefaultValue />
        )}
        {question?.question_type?.question_subtype === 'single_line' && (
          <Dropdown
            label="Validation Type"
            onChange={onChangeQuestionValidationType}
            options={questionValidationTypes}
            placeholder="Validation Type"
            value={question?.validation_type}
          />
        )}
        {hasDemographics && <BuilderSideMenuDemographicsOptions />}
      </Space>
    </Space>
  )
}

export default BuilderSideMenuEditQuestion
